/**
 * 景品カートの端末保存
 * 別のページへ移っても・再読み込みしても選んだ景品が残るよう、カートを端末に保存します。
 * 保存なし（null）と空のカート（[]）は別の意味です。空にしたカートは注文したときのカートへ戻しません。
 * 必要ptと在庫は保存値を信じず、画面が最新の景品カタログから導きます。
 */

import webStorage from '../../../shared/services/webStorage';
import { logWarn } from '../../../shared/utils/logger';

/** 保存キー */
const REWARD_CART_STORAGE_KEY = '@ikomasai/reward_cart';

/** カート項目 */
export type RewardCartItem = {
  /** カート項目キー（requestType:id） */
  key: string;
  /** 単品かセットか */
  requestType: 'single' | 'set';
  /** 景品IDまたは景品セットID */
  id: string;
  /** カートへ入れた時点の名前 */
  itemName: string;
  /** カートへ入れた時点の必要pt。計算には最新の景品カタログの値を使う */
  costPoints: number;
  /** 個数 */
  quantity: number;
};

/** 保存値 */
type StoredRewardCart = {
  /** 保存した来場者セッション。別のセッションには返さない */
  sessionToken: string;
  /** カート項目（JSONから読んだ時点では形が分からない） */
  items: unknown[];
};

/**
 * 保存値の形を確かめる（項目の中身は1件ずつ別に確かめる）
 * @param {unknown} value - JSONから読んだ値
 * @returns {value is StoredRewardCart} 使える形ならtrue
 */
const isStoredRewardCart = (value: unknown): value is StoredRewardCart => {
  const stored = value as StoredRewardCart | null;
  return Boolean(stored)
    && typeof stored.sessionToken === 'string'
    && Array.isArray(stored.items);
};

/**
 * カート項目の形を確かめる
 * @param {unknown} value - JSONから読んだ値
 * @returns {value is RewardCartItem} 使える形ならtrue
 */
const isRewardCartItem = (value: unknown): value is RewardCartItem => {
  const item = value as RewardCartItem | null;
  return Boolean(item)
    && (item.requestType === 'single' || item.requestType === 'set')
    && typeof item.id === 'string'
    && item.id !== ''
    && typeof item.itemName === 'string'
    && item.itemName !== ''
    && typeof item.costPoints === 'number'
    && Number.isFinite(item.costPoints)
    && item.costPoints >= 0
    && Number.isInteger(item.quantity)
    && item.quantity > 0
    && item.key === `${item.requestType}:${item.id}`;
};

/**
 * カート項目から保存する項目だけを取り出す（画面用の印を保存しない）
 * @param {RewardCartItem} item - カート項目
 * @returns {RewardCartItem} 保存する項目
 */
const pickStoredFields = ({ key, requestType, id, itemName, costPoints, quantity }: RewardCartItem): RewardCartItem => ({
  key,
  requestType,
  id,
  itemName,
  costPoints,
  quantity,
});

/**
 * 保存値を読む。壊れた値は消してnullを返す
 * @returns {Promise<StoredRewardCart | null>} 保存値
 */
const readStored = async (): Promise<StoredRewardCart | null> => {
  /** 保存した文字列 */
  const serialized = await webStorage.getItem(REWARD_CART_STORAGE_KEY);
  if (!serialized) {
    return null;
  }

  try {
    /** JSONから読んだ値 */
    const parsed: unknown = JSON.parse(serialized);
    if (isStoredRewardCart(parsed)) {
      return parsed;
    }
  } catch (error) {
    logWarn('保存していたカートが壊れていたため削除します。', {
      message: error instanceof Error ? error.message : String(error),
    });
  }
  await webStorage.removeItem(REWARD_CART_STORAGE_KEY);
  return null;
};

/**
 * 保存したカートを読み戻す
 * 別のセッションの保存は返さない。形の不正な項目は捨てる。
 * @param {string} sessionToken - 来場者セッショントークン
 * @returns {Promise<RewardCartItem[] | null>} カート項目（空にしたカートは[]）。保存が無ければnull
 */
export const loadRewardCart = async (sessionToken: string): Promise<RewardCartItem[] | null> => {
  try {
    /** 保存値 */
    const stored = await readStored();
    if (!stored || stored.sessionToken !== sessionToken) {
      return null;
    }
    return stored.items.filter(isRewardCartItem).map(pickStoredFields);
  } catch (error) {
    // 読めない端末では保存なしで動く。
    logWarn('保存していたカートを読めませんでした。', {
      message: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
};

/**
 * カートを保存する。空のカートも[]として保存する
 * @param {string} sessionToken - 来場者セッショントークン
 * @param {RewardCartItem[]} items - カート項目
 * @returns {Promise<void>}
 */
export const saveRewardCart = async (sessionToken: string, items: RewardCartItem[]): Promise<void> => {
  try {
    await webStorage.setItem(
      REWARD_CART_STORAGE_KEY,
      JSON.stringify({ sessionToken, items: items.map(pickStoredFields) } satisfies StoredRewardCart),
    );
  } catch (error) {
    // 保存できない端末では、画面を開いている間だけカートを持つ（従来どおり）。
    logWarn('カートを端末に保存できませんでした。', {
      message: error instanceof Error ? error.message : String(error),
    });
  }
};

/**
 * 保存したカートを消す（ログアウトした）
 * @returns {Promise<void>}
 */
export const clearRewardCart = async (): Promise<void> => {
  try {
    await webStorage.removeItem(REWARD_CART_STORAGE_KEY);
  } catch (error) {
    logWarn('保存していたカートを消せませんでした。', {
      message: error instanceof Error ? error.message : String(error),
    });
  }
};
